import { useState, useMemo, useRef, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { Check, ChevronsUpDown, Lock, LockOpen } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";

export interface Matter {
  matter: string;
  matter_id: string;
  client: string;
  description: string;
}

interface MatterSelectorProps {
  /** List of available matters to choose from */
  matters: Matter[];
  /** Currently selected matter ID (empty string = no selection) */
  value: string;
  /** Called when selection changes */
  onChange: (matterId: string) => void;
  /** Placeholder text when no matter is selected */
  placeholder?: string;
  /** Maximum number of matters to show in dropdown */
  maxResults?: number;
}

/**
 * A searchable dropdown for selecting a "hardcoded" matter.
 *
 * When a hardcoded matter is selected, all new time entries will
 * be automatically assigned to this matter.
 *
 * Features:
 * - Lock/unlock icon indicates selection state (locked = matter selected)
 * - Search/filter by matter number or description
 * - Shows "None" option to clear selection
 *
 * @example
 * ```tsx
 * <MatterSelector
 *   matters={matters}
 *   value={selectedMatterId}
 *   onChange={(id) => setSelectedMatterId(id)}
 * />
 * ```
 */
export function MatterSelector({
  matters,
  value,
  onChange,
  placeholder = "Hardcode a matter...",
  maxResults = 7,
}: MatterSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tooltip hover handlers - manually control to ignore focus events
  const handleTooltipMouseEnter = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!open) setTooltipOpen(true);
    }, 400);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setTooltipOpen(false);
  };

  // Close tooltip when popover opens
  const handlePopoverOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      // Clear tooltip state when popover opens
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      setTooltipOpen(false);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Determine if a matter is selected (locked)
  const isLocked = !!value;

  // Filter matters based on search term
  const filteredMatters = useMemo(() => {
    if (!searchTerm) {
      return matters.slice(0, maxResults);
    }
    const term = searchTerm.toLowerCase();
    return matters
      .filter(
        (matter) =>
          matter.matter.trim().toLowerCase().includes(term) ||
          matter.description.trim().toLowerCase().includes(term)
      )
      .slice(0, maxResults);
  }, [matters, searchTerm, maxResults]);

  // Get display text for current selection
  const selectedMatter = matters.find((m) => m.matter_id === value);
  const displayText = selectedMatter?.description || placeholder;

  const handleSelect = (matterId: string) => {
    onChange(matterId);
    setOpen(false);
  };

  return (
    <div
      className={cn(
        "group flex items-center rounded-md border transition-colors w-full h-10 relative",
        isLocked
          ? "border-blue-500 bg-blue-500/5 hover:bg-blue-500/10"
          : "border-border bg-card hover:bg-secondary"
      )}
    >
      {/* Blue dot indicator when matter is locked */}
      {isLocked && (
        <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-500 border-2 border-card" />
      )}
      {/* Lock/Unlock icon on left - clickable when locked to clear selection */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => isLocked && onChange("")}
              className={cn(
                "flex items-center justify-center w-9 h-full transition-colors",
                isLocked
                  ? "text-muted-foreground hover:text-foreground cursor-pointer"
                  : "text-muted-foreground cursor-default"
              )}
              aria-label={isLocked ? "Clear hardcoded matter" : "No matter selected"}
            >
              {isLocked ? (
                <Lock className="h-4 w-4" />
              ) : (
                <LockOpen className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isLocked ? "Click to clear" : "No matter locked"}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Popover open={open} onOpenChange={handlePopoverOpenChange} modal={true}>
        <TooltipProvider>
          <Tooltip open={tooltipOpen && !open}>
            <TooltipTrigger
              asChild
              onMouseEnter={handleTooltipMouseEnter}
              onMouseLeave={handleTooltipMouseLeave}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  role="combobox"
                  aria-expanded={open}
                  className="flex-1 justify-start text-left font-normal overflow-hidden border-0 rounded-none hover:bg-transparent text-foreground px-0"
                >
                  <span className="truncate">{displayText}</span>
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]">
              <p>All new entries will be assigned to this matter. Remember to clear when done.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent className="w-[475px] text-left p-0 bg-card border-border text-foreground">
          <Command className="bg-card border-border text-foreground">
            <CommandInput
              placeholder="Search matter..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              className="bg-card text-foreground border-border"
            />

            {/* Fixed "None" option - always visible above scrollable list */}
            <button
              type="button"
              onClick={() => handleSelect("")}
              className="flex items-center w-full px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  value === "" ? "opacity-100" : "opacity-0"
                )}
              />
              None
            </button>
            <div className="h-px bg-border mx-2" />

            <CommandList>
              <CommandEmpty>No matter found.</CommandEmpty>
              <CommandGroup className="bg-card border-border text-foreground">
                {/* Matter options */}
                {filteredMatters.map((matter, index) => (
                  <CommandItem
                    key={`matter-${index}`}
                    value={`${matter.matter.trim()} | ${matter.description.trim()} ${matter.client.trim()}`}
                    onSelect={() => handleSelect(matter.matter_id)}
                    className="hover:bg-accent hover:text-accent-foreground aria-selected:bg-accent aria-selected:text-accent-foreground"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === matter.matter_id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {matter.matter.trim()} | {matter.description.trim()} - {matter.client.trim()}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Chevrons icon on right */}
      <div className="flex items-center justify-center w-9 h-full text-muted-foreground">
        <ChevronsUpDown className="h-4 w-4" />
      </div>
    </div>
  );
}
